import { useCallback, useRef } from 'react';
import { parseScanCsv } from '../apigen/stress/csv';
import { endpointLabel } from '../apigen/stress/format';
import { formatSeconds, scheduleSeconds } from './format';
import type { QaApp, QaAppConfigDraft, QaApplicationOption, QaOptions } from './types';

const field =
  'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-200';

interface AppConfigEditorProps {
  app: QaApp;
  option: QaApplicationOption;
  options: QaOptions;
  draft: QaAppConfigDraft;
  onChange: (patch: Partial<QaAppConfigDraft>) => void;
}

/**
 * One application's card in the definition editor.
 *
 * The two kinds of application ask for genuinely different things. A scan
 * application's load is its CSV, so there is nothing to count and the only rate
 * control is the spacing between scans — which itself only means anything in
 * dummy mode, because a real-spend run goes out as one batch. Gen-api builds its
 * own inputs and has no CSV, so it alone still needs to be told how many calls
 * to make and how many at a time, plus which endpoint to hit.
 */
export function AppConfigEditor({ app, option, options, draft, onChange }: AppConfigEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isGenApi = app === 'genapi';
  const { limits } = options;

  const handleFileUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) =>
        onChange({
          scanIds: parseScanCsv(String(e.target?.result || '')),
          scanCsvName: file.name,
        });
      reader.readAsText(file);
    },
    [onChange],
  );

  const clearCsv = useCallback(() => {
    onChange({ scanIds: [], scanCsvName: undefined });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [onChange]);

  // Gen-api paces bursts of concurrent calls; a dummy scan run paces one request
  // per scan; a real-spend scan run is a single batch and schedules nothing.
  const schedule = isGenApi
    ? scheduleSeconds(draft.count, draft.burst_size, draft.burst_delay_seconds)
    : draft.dummy
      ? scheduleSeconds(draft.scanIds.length, 1, draft.burst_delay_seconds)
      : 0;

  return (
    <div
      className={`rounded-xl border p-4 transition-colors ${
        draft.enabled ? 'border-primary-200 bg-white' : 'border-slate-200 bg-slate-50/60'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={draft.enabled}
            disabled={!option.configured}
            onChange={(e) => onChange({ enabled: e.target.checked })}
            className="mt-0.5 rounded border-slate-300 text-primary-600 focus:ring-primary-400 disabled:opacity-40"
          />
          <span>
            <span className="text-sm font-semibold text-slate-900">{option.label}</span>
            <span className="block text-xs text-slate-500">
              {!draft.enabled
                ? 'Not part of this test'
                : isGenApi
                  ? `${draft.count} calls in bursts of ${draft.burst_size}` +
                    (draft.burst_delay_seconds > 0
                      ? `, ${draft.burst_delay_seconds}s apart`
                      : ', back to back') +
                    (schedule > 0 ? ` (${formatSeconds(schedule)} of submitting)` : '')
                  : draft.dummy
                    ? `${draft.scanIds.length} scans, one request each` +
                      (draft.burst_delay_seconds > 0
                        ? `, ${draft.burst_delay_seconds}s apart`
                        : ', back to back') +
                      (schedule > 0 ? ` (${formatSeconds(schedule)} of submitting)` : '')
                    : `${draft.scanIds.length} scans as a single batch`}
            </span>
          </span>
        </label>

        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
            draft.dummy ? 'bg-success-50 text-success-700' : 'bg-warning-50 text-warning-700'
          }`}
        >
          {draft.dummy ? 'Dummy' : 'Real spend'}
        </span>
      </div>

      {!option.configured && (
        <p className="mt-3 rounded-xl border border-warning-200 bg-warning-50 px-3 py-2 text-xs text-warning-700">
          No base URL is configured for this application, so it cannot be loaded from here. Set{' '}
          <span className="font-mono">{option.base_url_env.join(' or ')}</span> on the gen-api
          service.
        </p>
      )}

      {draft.enabled && option.configured && (
        <div className="mt-4 space-y-4">
          {isGenApi ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Total calls</span>
                  <input
                    type="number"
                    min={1}
                    max={limits.max_count}
                    value={draft.count}
                    onChange={(e) => onChange({ count: Number(e.target.value) })}
                    className={`mt-1 ${field}`}
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Burst size</span>
                  <input
                    type="number"
                    min={1}
                    max={limits.max_burst_size}
                    value={draft.burst_size}
                    onChange={(e) => onChange({ burst_size: Number(e.target.value) })}
                    className={`mt-1 ${field}`}
                  />
                  <span className="block text-xs text-slate-500 mt-1">
                    Submitted simultaneously
                  </span>
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Burst delay (seconds)</span>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    max={limits.max_burst_delay_seconds}
                    value={draft.burst_delay_seconds}
                    onChange={(e) => onChange({ burst_delay_seconds: Number(e.target.value) })}
                    className={`mt-1 ${field}`}
                  />
                  <span className="block text-xs text-slate-500 mt-1">Sets the request rate</span>
                </label>
              </div>

              <label className="block sm:max-w-xs">
                <span className="text-xs font-medium text-slate-600">Endpoint</span>
                <select
                  value={draft.model_type || options.default_model_type}
                  onChange={(e) => onChange({ model_type: e.target.value })}
                  className={`mt-1 ${field}`}
                >
                  {options.model_types.map((key) => (
                    <option key={key} value={key}>
                      {key === options.random_model_type
                        ? 'Randomized — every endpoint'
                        : endpointLabel(key)}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : (
            draft.dummy && (
              <label className="block sm:max-w-xs">
                <span className="text-xs font-medium text-slate-600">
                  Delay between scans (seconds)
                </span>
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  max={limits.max_burst_delay_seconds}
                  value={draft.burst_delay_seconds}
                  onChange={(e) => onChange({ burst_delay_seconds: Number(e.target.value) })}
                  className={`mt-1 ${field}`}
                />
                <span className="block text-xs text-slate-500 mt-1">
                  Each scan is submitted on its own, so this is the request rate.
                </span>
              </label>
            )
          )}

          <div className="rounded-lg border border-slate-200 p-3">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={draft.dummy}
                onChange={(e) => onChange({ dummy: e.target.checked })}
                className="mt-0.5 rounded border-slate-300 text-primary-600 focus:ring-primary-400"
              />
              <span className="text-sm text-slate-700">
                Dummy mode
                <span className="block text-xs text-slate-500">
                  Generations are simulated per job, so no deploy is needed and nothing is bought.
                  {isGenApi
                    ? ' Turning this off makes the run spend real money.'
                    : ' Scans go out one request at a time, so the test sets the rate. Turning this' +
                      ' off spends real money and submits the whole CSV as a single batch.'}
                </span>
              </span>
            </label>

            {draft.dummy && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">
                    Image generation delay (seconds)
                  </span>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    max={limits.max_dummy_delay_seconds}
                    value={draft.dummy_image_delay ?? ''}
                    placeholder="target default"
                    onChange={(e) =>
                      onChange({
                        dummy_image_delay: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                    className={`mt-1 ${field}`}
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">
                    Other call delay (seconds)
                  </span>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    max={limits.max_dummy_delay_seconds}
                    value={draft.dummy_other_delay ?? ''}
                    placeholder="target default"
                    onChange={(e) =>
                      onChange({
                        dummy_other_delay: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                    className={`mt-1 ${field}`}
                  />
                </label>
                <p className="sm:col-span-2 text-xs text-slate-500">
                  Leave blank to inherit whatever the target application is deployed with.
                </p>
              </div>
            )}
          </div>

          {!isGenApi && (
            <div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-600">Scans CSV</span>
                {draft.scanIds.length > 0 && (
                  <button
                    type="button"
                    onClick={clearCsv}
                    className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="relative mt-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileUpload}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center hover:border-primary-400 hover:bg-primary-50/30 transition-all">
                  {draft.scanIds.length > 0 ? (
                    <p className="text-sm text-slate-700">
                      <span className="font-semibold text-slate-900">{draft.scanIds.length}</span>{' '}
                      scan IDs loaded
                      {draft.scanCsvName && (
                        <span className="text-xs text-slate-400 font-mono">
                          {' '}
                          ({draft.scanCsvName})
                        </span>
                      )}
                    </p>
                  ) : (
                    <>
                      <p className="text-sm text-slate-700">
                        Drop a CSV here or{' '}
                        <span className="text-primary-600 font-medium">browse</span>
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        One scan ID per line, or an export with a scan_id column
                      </p>
                    </>
                  )}
                </div>
              </div>
              {draft.scanIds.length === 0 ? (
                <p className="mt-2 text-xs text-warning-700">
                  Without scan IDs this application is skipped at run time.
                </p>
              ) : (
                <p className="mt-2 text-xs text-slate-500">
                  The CSV is the load: every ID in it is submitted exactly once.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
